import Jate from 'jates';
import { ChevronDown, CircleCheck, CircleX } from 'lucide-react';
import { useState, useMemo } from 'react';
import { ApproveCsvPreview } from 'src/shared/components/thread-store/approval/ApproveCsvPreview';
import ApprovalValue from 'src/shared/components/thread-store/approval/ApprovalValue';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from 'src/shared/components/ui/accordion';
import { Button } from 'src/shared/components/ui/buttons/button';
import DynamicIcon from 'src/shared/components/ui/icons/dynamic-icon';
import { Separator } from 'src/shared/components/ui/separator';
import { useToast } from 'src/shared/components/ui/use-toast';
import { ApprovalStatus, ThreadStore } from 'src/features/flows/types/threadStore.types';
import { convertToDate } from 'src/shared/lib/date.utils';
import { useOrg } from 'src/shared/contexts/OrgContext';
import { detectCSV } from 'src/shared/lib/csv.utils';

interface ApprovalCardProps {
  flowId: string | undefined;
  approvalId: string | undefined;
  approvalStatus: ApprovalStatus;
  approvalProps: Record<string, any>;
  approvedDate?: number;
  threadStore?: ThreadStore;
  onResult?: (result: Exclude<ApprovalStatus, 'pending' | 'failed'>) => void;
  /** Whether to show download buttons. Default: true */
  showDownload?: boolean;
}

/**
 * Card component for human approval workflow.
 * Platform-agnostic - works in Outlook plugin, main frontend, and Chrome extension.
 */
export function ApprovalCard({
  flowId,
  approvalId,
  approvalStatus,
  approvalProps,
  approvedDate,
  threadStore,
  onResult,
  showDownload = true,
}: ApprovalCardProps) {
  const { orgFetch } = useOrg();
  const [approvalOpen, setApprovalOpen] = useState<string | undefined>(() =>
    ['pending', 'failed'].includes(approvalStatus) ? (approvalId ? approvalId : undefined) : undefined
  );
  const [valuesAccordionOpen, setValuesAccordionOpen] = useState<string[]>(() => Object.keys(approvalProps ?? {}));
  const [optimisticStatus, setOptimisticStatus] = useState<ApprovalStatus | null>(null);
  const [loading, setLoading] = useState<Exclude<ApprovalStatus, 'pending' | 'failed'> | null>(null);
  const { toast } = useToast();

  const csvApprovalProps = useMemo(() => {
    return Object.entries(approvalProps).filter(
      ([_key, value]) => value.type === 'csv' || (value.type === 'string' && detectCSV(value.value).isCSV)
    );
  }, [approvalProps]);

  const nonCsvApprovalProps = useMemo(() => {
    return Object.entries(approvalProps).filter(
      ([_key, value]) => value.type !== 'csv' && !(value.type === 'string' && detectCSV(value.value).isCSV)
    );
  }, [approvalProps]);

  const statusToRender = optimisticStatus ?? approvalStatus;

  const handleApprovalAction = async (result: Exclude<ApprovalStatus, 'pending' | 'failed'>) => {
    try {
      if (!flowId || !approvalId) {
        throw new Error('Failed to approve');
      }

      setLoading(result);

      const res = await orgFetch<any>(`/api/flow/v2/${flowId}/approval/${approvalId}?result=${result}`, {
        method: 'post',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!res.ok) {
        throw new Error('Failed to approve');
      }

      const data = await res.json();
      if (data.error) {
        throw new Error(data.error);
      }

      setOptimisticStatus(result);
      if (onResult) onResult(result);
      setLoading(null);
      setApprovalOpen(undefined);
    } catch (err) {
      console.error(err);
      setOptimisticStatus(null);
      setLoading(null);
      toast({
        title: 'Approval failed',
        description: 'An error happened while trying to approve or reject.',
        variant: 'error',
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="border p-4 gap-2 rounded-sm bg-white">
        <Accordion
          type="single"
          collapsible
          value={approvalOpen}
          onValueChange={(value) => setApprovalOpen(value)}
          className="mb-2"
        >
          <AccordionItem value={approvalId || ''}>
            <AccordionTrigger headless className="group w-full mb-2">
              <div className="flex w-full items-center gap-2">
                <div className="shrink-0 size-7 flex-center rounded-sm bg-dashboardblue-50">
                  <DynamicIcon name="UserCheck" size={15} className="shrink-0 stroke-2 text-dashboardblue" />
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-bold leading-[14px]">Human Approval</p>
                </div>
                <ChevronDown
                  size={15}
                  className="shrink-0 text-typography-darkgrey transition-transform duration-200 group-data-[state=open]:rotate-180"
                />
                {['approved', 'rejected'].includes(statusToRender) && (
                  <div className="ml-auto shrink-0 rounded-sm flex-center size-5 bg-success-600/10">
                    {statusToRender === 'approved' ? (
                      <CircleCheck size={15} className="stroke-success-600" />
                    ) : (
                      <CircleX size={15} className="stroke-destructive-600" />
                    )}
                  </div>
                )}
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-2 flex flex-col gap-2">
              {csvApprovalProps.length > 0 && (
                <>
                  <Separator />
                  <Accordion type="multiple" value={valuesAccordionOpen} onValueChange={setValuesAccordionOpen}>
                    {csvApprovalProps.map(([key, value]) => (
                      <ApproveCsvPreview
                        key={key}
                        containsHeader={value.containsHeader}
                        label={value.label || key}
                        value={value.value || ''}
                        valueToApproveId={key}
                        threadStore={threadStore}
                        showDownload={showDownload}
                        description={value.description}
                      />
                    ))}
                  </Accordion>
                </>
              )}
              {nonCsvApprovalProps.length > 0 && (
                <>
                  <Separator />
                  <Accordion type="multiple" value={valuesAccordionOpen} onValueChange={setValuesAccordionOpen}>
                    {nonCsvApprovalProps.map(([key, value]) => (
                      <ApprovalValue
                        key={key}
                        id={key}
                        label={value.label || key}
                        type={value.type}
                        value={value.value}
                        description={value.description}
                      />
                    ))}
                  </Accordion>
                </>
              )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
        {['pending', 'failed'].includes(statusToRender) && (
          <div className="flex flex-col w-full gap-2">
            <Button
              className="mt-1"
              variant="outline"
              disabled={!!loading}
              onClick={() => handleApprovalAction('approved')}
            >
              {loading === 'approved' ? 'Approving…' : 'Approve'}
            </Button>
            <Button variant="outline" disabled={!!loading} onClick={() => handleApprovalAction('rejected')}>
              {loading === 'rejected' ? 'Declining…' : 'Decline'}
            </Button>
          </div>
        )}
        {['approved', 'rejected'].includes(statusToRender) && (
          <span className="text-typography-darkgrey text-sm my-4">
            {`${statusToRender === 'approved' ? 'Approved' : 'Declined'} on ${new Jate(convertToDate(approvedDate ?? Date.now())!).format('d MMM yy, HH:mm:ss')}`}
          </span>
        )}
      </div>
    </div>
  );
}

export default ApprovalCard;
